import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ClipboardCheck, Pencil, Phone, Plus, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, money, telLink } from "@/lib/api";
import { PageHead } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { P } from "@/components/studio/portalKit";
import { CustomerFormDialog } from "@/components/studio/CustomerFormDialog";

const StageChip = ({ s, onToggle, testid }) => (
  <button type="button" data-testid={testid} onClick={onToggle}
    className={`flex min-h-[44px] items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm font-medium transition-colors ${s.done ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-red-200 bg-red-50 text-red-700"}`}>
    {s.done ? <Check className="h-4 w-4 shrink-0" strokeWidth={3} /> : <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-red-500" />}
    {s.label}
  </button>
);

const CustomerTile = ({ c, onChange, onEdit }) => {
  const nav = useNavigate();
  const toggle = async (s) => {
    try { const { data } = await api.post(`${P}/customers/${c.id}/stage`, { stage: s.key, done: !s.done }); onChange(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async () => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    try { await api.delete(`${P}/customers/${c.id}`); onChange(null, c.id); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Card data-testid={`studio-customer-${c.id}`} className="w-full overflow-hidden">
      <div className={`h-1 ${c.pending ? "bg-red-400" : "bg-emerald-500"}`} />
      <CardContent className="space-y-4 p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-lg font-semibold text-brand-ink">{c.name}</p>
            <a href={telLink(c.mobile)} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:underline"><Phone className="h-3.5 w-3.5" />{c.mobile}</a>
            {c.email && <span className="ml-2 text-sm text-slate-500">{c.email}</span>}
            <p className="text-sm text-slate-600">{[c.brand, c.model, c.reg_no].filter(Boolean).join(" · ") || "Vehicle not set"}</p>
            {c.source === "central_sales" && <p className="text-xs font-medium text-crimson-600">From Central Sales Team</p>}
          </div>
          <div className="flex shrink-0 flex-wrap justify-end gap-1">
            <Button size="sm" className="h-10 bg-crimson-500 hover:bg-crimson-600" data-testid={`register-customer-${c.id}`} onClick={() => nav(`/studio/warranty?customer=${c.id}`)}><ShieldCheck className="mr-1 h-4 w-4" />Register</Button>
            <Button size="icon" variant="ghost" className="h-10 w-10" data-testid={`inspect-customer-${c.id}`} onClick={() => nav(`/studio/inspection?customer=${c.id}`)} aria-label="Inspection"><ClipboardCheck className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="h-10 w-10" data-testid={`edit-customer-${c.id}`} onClick={() => onEdit(c)} aria-label="Edit"><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="h-10 w-10" data-testid={`delete-customer-${c.id}`} onClick={del} aria-label="Delete"><Trash2 className="h-4 w-4 text-slate-400" /></Button>
          </div>
        </div>
        <div className="space-y-1 rounded-lg bg-slate-50 p-3 text-sm">
          {c.services.map((s, i) => <div key={i} className="flex justify-between gap-3"><span>{s.name}</span><span>{money(s.price)}</span></div>)}
          <div className="flex justify-between border-t border-slate-200 pt-1 font-bold"><span>Total</span><span data-testid={`customer-total-${c.id}`}>{money(c.total)}</span></div>
        </div>
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
          {c.stages.map((s) => <StageChip key={s.key} s={s} onToggle={() => toggle(s)} testid={`stage-${s.key}-${c.id}`} />)}
        </div>
      </CardContent>
    </Card>
  );
};

export default function StudioCustomers() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(undefined);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const load = () => api.get(`${P}/customers`).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const onChange = (d, removedId) => setRows((r) => (removedId ? r.filter((x) => x.id !== removedId) : r.map((x) => (x.id === d.id ? d : x))));
  const list = rows.filter((c) => `${c.name} ${c.mobile} ${c.reg_no} ${c.model}`.toLowerCase().includes(q.toLowerCase())
    && (filter === "all" || (filter === "pending" ? c.pending > 0 : c.pending === 0)));
  return (
    <div className="space-y-5">
      <PageHead title="Customers" subtitle="Each customer with their stages — red is waiting, green is done. Tap a stage to update it.">
        <Button data-testid="add-studio-customer-btn" onClick={() => setEdit(null)} className="h-11 bg-crimson-500 hover:bg-crimson-600"><Plus className="mr-1.5 h-4 w-4" />Add customer</Button>
      </PageHead>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input data-testid="studio-customer-search" placeholder="Search name, mobile, reg no." value={q} onChange={(e) => setQ(e.target.value)} className="h-11 sm:max-w-sm" />
        <div className="flex gap-2">
          {[["all", "All"], ["pending", "Waiting"], ["done", "Completed"]].map(([k, l]) => (
            <button key={k} data-testid={`customer-filter-${k}`} onClick={() => setFilter(k)}
              className={`min-h-[40px] rounded-full px-4 text-sm font-semibold transition-colors ${filter === k ? "bg-brand-deep text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
          ))}
        </div>
      </div>
      {list.length === 0 && <p className="text-sm text-slate-500">No customers yet. Add one, or add a Central Sales job from Central Sales Jobs.</p>}
      <div className="space-y-4">{list.map((c) => <CustomerTile key={c.id} c={c} onChange={onChange} onEdit={setEdit} />)}</div>
      {edit !== undefined && <CustomerFormDialog row={edit} onClose={() => setEdit(undefined)} onSaved={() => { setEdit(undefined); load(); }} />}
    </div>
  );
}
